import React, { useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { SmartphoneIcon, ShieldCheckIcon, GlobeIcon } from 'lucide-react';
export function FeaturesDMRV() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section ref={ref} className="w-full bg-[#FAFDF7] py-20 sm:py-28" data-id="element-901">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-902">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-903">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-carbon-deep tracking-tight" data-id="element-904">
            <span className="text-carbon-bright" data-id="element-905">Independent dMRV</span> that
            bridges continents
          </h2>
          <p className="mt-4 text-lg text-carbon-mid max-w-2xl mx-auto" data-id="element-906">
            From smallholder farms in Uganda to EU import desks. One platform
            that captures, verifies, and certifies every step of the supply
            chain.
          </p>
        </motion.div>

        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={containerVariants} data-id="element-907">
          <motion.article className="relative overflow-hidden p-8 rounded-2xl border border-carbon-geo bg-white cursor-pointer" variants={cardVariants} whileHover={{
          y: -6,
          boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.3)',
          transition: {
            duration: 0.25,
            ease: 'easeOut'
          }
        }} data-id="element-908">
            <svg className="absolute -top-2 -right-2 w-32 h-32 text-carbon-bright opacity-[0.06]" viewBox="0 0 120 120" fill="none" aria-hidden="true" data-id="element-909">
              <rect x="38" y="10" width="44" height="80" rx="8" stroke="currentColor" strokeWidth="3" data-id="element-910" />
              <rect x="48" y="18" width="24" height="50" rx="2" fill="currentColor" fillOpacity="0.3" data-id="element-911" />
              <circle cx="60" cy="80" r="4" fill="currentColor" data-id="element-912" />
              <path d="M80 30 C88 30 94 24 94 16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" data-id="element-913" />
              <path d="M80 22 C84 22 88 19 88 14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" data-id="element-914" />
              <circle cx="80" cy="30" r="2" fill="currentColor" data-id="element-915" />
            </svg>
            <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-6" data-id="element-916">
              <SmartphoneIcon className="w-6 h-6 text-carbon-bright" data-id="element-917" />
            </div>
            <h3 className="text-xl font-bold text-carbon-deep mb-3" data-id="element-918">
              Mobile-First Data Capture
            </h3>
            <p className="text-carbon-mid leading-relaxed" data-id="element-919">
              Farmers log harvest data via USSD on any phone or our mobile app,
              no internet required. GPS-tagged entries capture origin, quality
              grade, and processing details at the source.
            </p>
          </motion.article>

          <motion.article className="relative overflow-hidden p-8 rounded-2xl border border-carbon-geo bg-white cursor-pointer" variants={cardVariants} whileHover={{
          y: -6,
          boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.3)',
          transition: {
            duration: 0.25,
            ease: 'easeOut'
          }
        }} data-id="element-920">
            <svg className="absolute -top-3 -right-3 w-36 h-36 text-carbon-bright opacity-[0.06]" viewBox="0 0 120 120" fill="none" aria-hidden="true" data-id="element-921">
              <rect x="15" y="10" width="70" height="90" rx="6" stroke="currentColor" strokeWidth="2.5" data-id="element-922" />
              <line x1="28" y1="30" x2="72" y2="30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-923" />
              <line x1="28" y1="42" x2="65" y2="42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-924" />
              <line x1="28" y1="54" x2="58" y2="54" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-925" />
              <circle cx="90" cy="85" r="22" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth="2.5" data-id="element-926" />
              <path d="M82 85 L88 91 L100 79" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-927" />
            </svg>
            <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-6" data-id="element-928">
              <ShieldCheckIcon className="w-6 h-6 text-carbon-bright" data-id="element-929" />
            </div>
            <h3 className="text-xl font-bold text-carbon-deep mb-3" data-id="element-930">
              Digital Quality Certificates
            </h3>
            <p className="text-carbon-mid leading-relaxed" data-id="element-931">
              Every verified harvest generates a tamper-proof Digital Quality
              Certificate. International buyers scan, verify, and trust the
              origin, quality, and compliance status of every shipment.
            </p>
          </motion.article>

          <motion.article className="relative overflow-hidden p-8 rounded-2xl border border-carbon-geo bg-white cursor-pointer" variants={cardVariants} whileHover={{
          y: -6,
          boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.3)',
          transition: {
            duration: 0.25,
            ease: 'easeOut'
          }
        }} data-id="element-932">
            <svg className="absolute -top-4 -right-4 w-40 h-40 text-carbon-bright opacity-[0.06]" viewBox="0 0 140 140" fill="none" aria-hidden="true" data-id="element-933">
              <circle cx="70" cy="65" r="45" stroke="currentColor" strokeWidth="2.5" data-id="element-934" />
              <ellipse cx="70" cy="65" rx="20" ry="45" stroke="currentColor" strokeWidth="1.5" data-id="element-935" />
              <line x1="25" y1="50" x2="115" y2="50" stroke="currentColor" strokeWidth="1.5" data-id="element-936" />
              <line x1="25" y1="80" x2="115" y2="80" stroke="currentColor" strokeWidth="1.5" data-id="element-937" />
              <circle cx="30" cy="115" r="8" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5" data-id="element-938" />
              <circle cx="110" cy="115" r="8" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5" data-id="element-939" />
              <path d="M50 100 L30 115" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" data-id="element-940" />
              <path d="M90 100 L110 115" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" data-id="element-941" />
            </svg>
            <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-6" data-id="element-942">
              <GlobeIcon className="w-6 h-6 text-carbon-bright" data-id="element-943" />
            </div>
            <h3 className="text-xl font-bold text-carbon-deep mb-3" data-id="element-944">
              Trade & Finance Compliance
            </h3>
            <p className="text-carbon-mid leading-relaxed" data-id="element-945">
              Automated EUDR due diligence, UCDA export certification, and trade
              finance documentation. One platform bridges Africa's producers
              with EU market access requirements.
            </p>
          </motion.article>
        </motion.div>
      </div>
    </section>;
}